import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaVideo, FaLaptop, FaUsers, FaBookOpen, FaShieldAlt, FaShip, FaChartLine, FaArrowRight } from 'react-icons/fa';
import { getCourses } from '../services/api';
import { Link } from 'react-router-dom';
import Loader from '../components/Loader';
import Reveal from '../components/Reveal';
import { gridContainer, gridItem, cardHover, heroContainer, viewportOnce } from '../components/motionPresets';
import { useT } from '../i18n/LanguageContext';
import './Training.css';

const T = {
    en: {
        heroTitle: 'Online Training Sessions',
        heroSubtitle: 'Equipping you with knowledge, skills, and resources for successful importation',
        introTitle: 'Master the Art of Importation',
        introText: "Are you struggling with the complexities of importing goods? Many face the same hurdles: tangled regulations, unreliable suppliers, and overwhelming logistics. We're here to guide you through every step of the importing journey with comprehensive training programs.",
        offerTitle: 'What We Offer',
        featureOnlineTitle: 'Online & Onsite Support',
        featureOnlineDesc: "Personalized support whether you're a newbie or seasoned importer. Our instructors guide you through every step.",
        featureVideoTitle: 'Video Tutorials & Courses',
        featureVideoDesc: 'Extensive library covering beginners guides, best practices, pricing strategies, and supplier connections.',
        featureWorkshopTitle: 'Hands-On Workshops',
        featureWorkshopDesc: 'Live demonstrations and interactive experiences with instructors and fellow learners for practical insights.',
        featureRegulationsTitle: 'Local Regulations',
        featureRegulationsDesc: 'Specialized modules on local laws and customs regulations to keep you informed and compliant.',
        featureRiskTitle: 'Risk Management',
        featureRiskDesc: 'Effective risk management strategies with contingency plans to pivot smoothly when obstacles arise.',
        featureShippingTitle: 'Shipping & Logistics',
        featureShippingDesc: 'Everything from shipping options and cost management to tracking shipments and customs clearance.',
        coursesTitle: 'Available Courses',
        filterAll: 'All',
        filterOnline: 'Online',
        filterVideo: 'Video',
        filterOnsite: 'Onsite',
        loadingCourses: 'Loading courses',
        emptyCourses: 'No courses available in this category yet.',
        enrollNow: 'Enroll Now',
        ctaTitle: 'Continuous Learning is Essential',
        ctaText: 'In a rapidly changing import landscape, stay updated with the latest trends and best practices.',
        joinCommunity: 'Join Our Community',
    },
    fr: {
        heroTitle: 'Sessions de formation en ligne',
        heroSubtitle: "Vous doter des connaissances, des compétences et des ressources pour une importation réussie",
        introTitle: "Maîtrisez l'art de l'importation",
        introText: "Vous peinez face à la complexité de l'importation de marchandises ? Beaucoup rencontrent les mêmes obstacles : des réglementations enchevêtrées, des fournisseurs peu fiables et une logistique accablante. Nous sommes là pour vous accompagner à chaque étape de votre parcours d'importation grâce à des programmes de formation complets.",
        offerTitle: 'Ce que nous proposons',
        featureOnlineTitle: 'Assistance en ligne et sur site',
        featureOnlineDesc: "Une assistance personnalisée, que vous soyez débutant ou importateur chevronné. Nos formateurs vous guident à chaque étape.",
        featureVideoTitle: 'Tutoriels vidéo et cours',
        featureVideoDesc: "Une vaste bibliothèque couvrant les guides pour débutants, les bonnes pratiques, les stratégies de tarification et les mises en relation avec les fournisseurs.",
        featureWorkshopTitle: 'Ateliers pratiques',
        featureWorkshopDesc: "Des démonstrations en direct et des expériences interactives avec les formateurs et les autres apprenants pour des enseignements concrets.",
        featureRegulationsTitle: 'Réglementations locales',
        featureRegulationsDesc: "Des modules spécialisés sur les lois locales et les réglementations douanières pour vous tenir informé et en conformité.",
        featureRiskTitle: 'Gestion des risques',
        featureRiskDesc: "Des stratégies efficaces de gestion des risques avec des plans de contingence pour pivoter sereinement lorsque des obstacles surviennent.",
        featureShippingTitle: 'Expédition et logistique',
        featureShippingDesc: "De la sélection des options d'expédition à la maîtrise des coûts, en passant par le suivi des colis et le dédouanement.",
        coursesTitle: 'Cours disponibles',
        filterAll: 'Tous',
        filterOnline: 'En ligne',
        filterVideo: 'Vidéo',
        filterOnsite: 'Sur site',
        loadingCourses: 'Chargement des cours',
        emptyCourses: "Aucun cours disponible dans cette catégorie pour le moment.",
        enrollNow: "S'inscrire",
        ctaTitle: "L'apprentissage continu est essentiel",
        ctaText: "Dans un environnement d'importation en constante évolution, restez à jour des dernières tendances et bonnes pratiques.",
        joinCommunity: 'Rejoignez notre communauté',
    },
};

function Training() {
    const t = useT(T);
    const [courses, setCourses] = useState([]);
    const [filter, setFilter] = useState('all');
    const [loadingCourses, setLoadingCourses] = useState(true);

    useEffect(() => {
        getCourses()
            .then(res => setCourses(res.data))
            .catch(() => {})
            .finally(() => setLoadingCourses(false));
    }, []);

    const filteredCourses = filter === 'all' ? courses : courses.filter(c => c.course_type === filter);

    const features = [
        { icon: <FaLaptop />, title: t.featureOnlineTitle, desc: t.featureOnlineDesc },
        { icon: <FaVideo />, title: t.featureVideoTitle, desc: t.featureVideoDesc },
        { icon: <FaUsers />, title: t.featureWorkshopTitle, desc: t.featureWorkshopDesc },
        { icon: <FaBookOpen />, title: t.featureRegulationsTitle, desc: t.featureRegulationsDesc },
        { icon: <FaShieldAlt />, title: t.featureRiskTitle, desc: t.featureRiskDesc },
        { icon: <FaShip />, title: t.featureShippingTitle, desc: t.featureShippingDesc },
    ];

    const filterLabels = { all: t.filterAll, online: t.filterOnline, video: t.filterVideo, onsite: t.filterOnsite };

    return (
        <div className="training-page">
            <motion.section className="page-hero" initial="hidden" animate="show" variants={heroContainer}>
                <motion.h1 variants={gridItem}>{t.heroTitle}</motion.h1>
                <motion.p variants={gridItem}>{t.heroSubtitle}</motion.p>
            </motion.section>

            <section className="training-intro">
                <div className="section-container">
                    <Reveal className="intro-text">
                        <h2>{t.introTitle}</h2>
                        <p>{t.introText}</p>
                    </Reveal>
                </div>
            </section>

            {/* Features */}
            <section className="features-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.offerTitle}</h2></Reveal>
                    <motion.div className="features-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        {features.map((feature, index) => (
                            <motion.div key={index} className="feature-card" variants={gridItem} whileHover={cardHover}>
                                <div className="feature-icon">{feature.icon}</div>
                                <h3>{feature.title}</h3>
                                <p>{feature.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Courses */}
            <section className="courses-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.coursesTitle}</h2></Reveal>
                    <div className="course-filters">
                        {['all', 'online', 'video', 'onsite'].map(type => (
                            <button
                                key={type}
                                className={`cat-btn ${filter === type ? 'active' : ''}`}
                                onClick={() => setFilter(type)}
                            >
                                {filterLabels[type]}
                            </button>
                        ))}
                    </div>
                    {loadingCourses && <Loader label={t.loadingCourses} />}
                    {!loadingCourses && filteredCourses.length === 0 && (
                        <p className="empty-state" style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
                            {t.emptyCourses}
                        </p>
                    )}
                    {!loadingCourses && filteredCourses.length > 0 && (
                    <motion.div className="courses-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        {filteredCourses.map(course => (
                            <motion.div key={course.id} className="course-card" variants={gridItem} whileHover={cardHover}>
                                {course.image_url && (
                                    <img src={course.image_url} alt={course.title} className="course-card-img" />
                                )}
                                <div className="course-type-badge">{course.course_type}</div>
                                <h3>{course.title}</h3>
                                <p>{course.description}</p>
                                <div className="course-meta">
                                    <span className="course-duration">{course.duration}</span>
                                    <span className="course-price">{Number(course.price).toLocaleString()} XAF</span>
                                </div>
                                <button className="btn btn-primary course-enroll-btn">{t.enrollNow}</button>
                            </motion.div>
                        ))}
                    </motion.div>
                    )}
                </div>
            </section>

            <section className="cta-section">
                <Reveal className="cta-content" y={36}>
                    <FaChartLine className="cta-icon" />
                    <h2>{t.ctaTitle}</h2>
                    <p>{t.ctaText}</p>
                    <Link to="/contact" className="btn btn-primary">
                        {t.joinCommunity} <FaArrowRight />
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}

export default Training;
